import type { IsoDate, RecurringTemplate, Transaction } from './model';
import { spendingOf } from './spending';
import { shiftMonth, type MonthKey } from './analytics';

/**
 * Anomaly detection (V1.5): what in a month is out of the ordinary for this
 * budget. Pure — screens pass entries, nothing here reads a database.
 *
 * Four kinds, each with its own rule so one surprise is reported once:
 *  - category: a subcategory's everyday spending (bill payments left out)
 *    already above its usual whole month — the median of the months before
 *    (up to six, at least three inside the budget's history) — by half again
 *    and by more than its usual spread; or spending where there usually is
 *    none. The month so far is compared with a usual *whole* month, so a
 *    month in progress is only flagged once it has really gone past.
 *  - entry: one everyday entry several times the usual entry of its
 *    subcategory (median of at least five earlier ones). When such entries
 *    explain most of their subcategory's jump, only the entries are shown.
 *  - bill: a recorded bill a quarter or more above its last recordings
 *    (mean of up to three, at least two).
 *  - duplicate: two identical entries saved within two minutes — most
 *    likely a double tap.
 *
 * Money floors keep noise out: a move has to matter against the budget's
 * usual month (5 % for categories and entries, 2 % for a bill).
 * Medians and MAD (median absolute deviation) are used rather than means
 * and standard deviations: one odd month in the history must not hide the
 * next one.
 */
export type AnomalyKind = 'category' | 'entry' | 'bill' | 'duplicate';

interface Base {
  /** Stable across recomputes (for "not unusual" dismissals). */
  key: string;
  kind: AnomalyKind;
  categoryId: string;
  subcategoryId: string | null;
  /** The amount found (this month's subcategory total, the entry, the bill). */
  amount: number;
  /** What is usual; 0 for something new. */
  usual: number;
  /** How far past usual, in minor units: what the list is ranked by. */
  excess: number;
}

export type Anomaly =
  | (Base & { kind: 'category'; isNew: boolean; months: number })
  | (Base & { kind: 'entry'; entry: Transaction; sample: number })
  | (Base & { kind: 'bill'; entry: Transaction; template: RecurringTemplate | null })
  | (Base & { kind: 'duplicate'; entry: Transaction; first: Transaction });

export interface AnomalyInput {
  month: MonthKey;
  /** Entries dated after today are ignored (a month in progress is judged so far). */
  today: IsoDate;
  /** Live entries of the twelve months before `month` and `month` itself. */
  entries: readonly Transaction[];
  /** Bills by id, for their names; a missing one still counts. */
  templates?: ReadonlyMap<string, RecurringTemplate>;
  /** The budget's first entry: months before it are not history. */
  firstDate: IsoDate | null;
}

const HISTORY_MONTHS = 6;
const MIN_HISTORY_MONTHS = 3;
const MIN_ENTRY_SAMPLE = 5;
const DOUBLE_TAP_MS = 2 * 60 * 1000;

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

/** Median absolute deviation, scaled to be comparable with a standard deviation. */
export function mad(values: readonly number[]): number {
  const m = median(values);
  return 1.4826 * median(values.map((v) => Math.abs(v - m)));
}

const subKey = (e: Pick<Transaction, 'categoryId' | 'subcategoryId'>) => `${e.categoryId}/${e.subcategoryId ?? ''}`;

export function findAnomalies({ month, today, entries, templates = new Map(), firstDate }: AnomalyInput): Anomaly[] {
  const cut = month === today.slice(0, 7) ? today : `${month}-31`;
  const start = firstDate?.slice(0, 7) ?? null;
  const history = Array.from({ length: HISTORY_MONTHS }, (_, i) => shiftMonth(month, -(i + 1))).filter((m) => start !== null && m >= start);
  const inHistory = new Set(history);

  const now: Transaction[] = [];
  const monthTotals = new Map<MonthKey, number>();
  // subcategory → month → everyday spending
  const everyday = new Map<string, Map<MonthKey, number>>();
  // subcategory → earlier everyday entry amounts (the twelve months before `month`)
  const earlierEntries = new Map<string, number[]>();
  // bill → earlier recordings, newest first after sorting
  const earlierBills = new Map<string, Transaction[]>();

  for (const e of entries) {
    const m = e.date.slice(0, 7);
    if (m > month || (m === month && e.date > cut)) continue;
    const v = spendingOf(e);
    if (m === month) now.push(e);
    if (v === 0) continue;
    if (inHistory.has(m)) monthTotals.set(m, (monthTotals.get(m) ?? 0) + v);
    if (e.recurringTemplateId) {
      if (m < month) {
        const list = earlierBills.get(e.recurringTemplateId) ?? [];
        list.push(e);
        earlierBills.set(e.recurringTemplateId, list);
      }
      continue;
    }
    const key = subKey(e);
    if (m === month || inHistory.has(m)) {
      let byMonth = everyday.get(key);
      if (!byMonth) everyday.set(key, (byMonth = new Map()));
      byMonth.set(m, (byMonth.get(m) ?? 0) + v);
    }
    if (m < month && v > 0) {
      const list = earlierEntries.get(key) ?? [];
      list.push(v);
      earlierEntries.set(key, list);
    }
  }

  const found: Anomaly[] = [];

  // Duplicates need no history: the same entry saved twice within two minutes.
  const seen = new Map<string, Transaction[]>();
  for (const e of [...now].sort((a, b) => a.createdAt - b.createdAt)) {
    if (e.recurringTemplateId || spendingOf(e) === 0) continue;
    const sig = [e.date, subKey(e), e.type, e.amountMinor, e.paidBy].join('|');
    const twins = seen.get(sig) ?? [];
    const first = twins.find((t) => e.createdAt - t.createdAt <= DOUBLE_TAP_MS);
    if (first) {
      found.push({
        key: `duplicate:${e.id}`,
        kind: 'duplicate',
        categoryId: e.categoryId,
        subcategoryId: e.subcategoryId,
        amount: e.amountMinor,
        usual: 0,
        excess: e.amountMinor,
        entry: e,
        first,
      });
    }
    twins.push(e);
    seen.set(sig, twins);
  }

  if (history.length < MIN_HISTORY_MONTHS) return rank(found);
  const usualMonth = median(history.map((m) => Math.max(0, monthTotals.get(m) ?? 0)));
  if (usualMonth <= 0) return rank(found);
  const floor = 0.05 * usualMonth;

  // Single entries far above the usual entry of their subcategory.
  const entryAnomalies: Extract<Anomaly, { kind: 'entry' }>[] = [];
  for (const e of now) {
    const v = spendingOf(e);
    if (e.recurringTemplateId || v <= 0) continue;
    const sample = earlierEntries.get(subKey(e)) ?? [];
    if (sample.length < MIN_ENTRY_SAMPLE) continue;
    const usual = median(sample);
    const limit = Math.max(3 * usual, usual + 5 * mad(sample));
    if (v >= limit && v - usual >= floor) {
      entryAnomalies.push({
        key: `entry:${e.id}`,
        kind: 'entry',
        categoryId: e.categoryId,
        subcategoryId: e.subcategoryId,
        amount: v,
        usual: Math.round(usual),
        excess: Math.round(v - usual),
        entry: e,
        sample: sample.length,
      });
    }
  }

  // Subcategories past their usual whole month.
  for (const [key, byMonth] of everyday) {
    const current = byMonth.get(month) ?? 0;
    if (current <= 0) continue;
    const values = history.map((m) => Math.max(0, byMonth.get(m) ?? 0));
    const usual = median(values);
    const [categoryId, sub] = key.split('/') as [string, string];
    const subcategoryId = sub === '' ? null : sub;
    let flagged: Extract<Anomaly, { kind: 'category' }> | null = null;
    if (usual === 0) {
      // Usually nothing here (at most one of the months before had any): new spending, if it is a real amount.
      if (values.filter((v) => v > 0).length <= 1 && current >= 2 * floor) {
        flagged = {
          key: `category:${month}:${key}`,
          kind: 'category',
          categoryId,
          subcategoryId,
          amount: current,
          usual: 0,
          excess: current,
          isNew: true,
          months: history.length,
        };
      }
    } else if (current >= Math.max(1.5 * usual, usual + 3 * mad(values)) && current - usual >= floor) {
      flagged = {
        key: `category:${month}:${key}`,
        kind: 'category',
        categoryId,
        subcategoryId,
        amount: current,
        usual: Math.round(usual),
        excess: Math.round(current - usual),
        isNew: false,
        months: history.length,
      };
    }
    if (!flagged) continue;
    // One big purchase that explains the jump is the better story: show the entry, not the category.
    const explained = entryAnomalies.filter((a) => subKey(a) === key).reduce((s, a) => s + a.excess, 0);
    if (explained >= 0.5 * flagged.excess) continue;
    found.push(flagged);
  }
  found.push(...entryAnomalies);

  // Bills recorded well above their last recordings.
  for (const e of now) {
    if (!e.recurringTemplateId) continue;
    const v = spendingOf(e);
    const before = (earlierBills.get(e.recurringTemplateId) ?? []).sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 3);
    if (v <= 0 || before.length < 2) continue;
    const usual = before.reduce((s, b) => s + spendingOf(b), 0) / before.length;
    if (usual > 0 && v >= 1.25 * usual && v - usual >= 0.02 * usualMonth) {
      found.push({
        key: `bill:${e.id}`,
        kind: 'bill',
        categoryId: e.categoryId,
        subcategoryId: e.subcategoryId,
        amount: v,
        usual: Math.round(usual),
        excess: Math.round(v - usual),
        entry: e,
        template: templates.get(e.recurringTemplateId) ?? null,
      });
    }
  }

  return rank(found);
}

/** Double taps first (something to fix), then the biggest surprises. */
function rank(list: Anomaly[]): Anomaly[] {
  return list.sort((a, b) => Number(b.kind === 'duplicate') - Number(a.kind === 'duplicate') || b.excess - a.excess || a.key.localeCompare(b.key));
}
